// Shared Claude API calling logic used by Loading.jsx, Results.jsx, and Activities.jsx
// Single source of truth for all Claude API network calls from the frontend

import { API_BASE } from './config';
import { track } from './analytics';

// ---------------------------------------------------------------------------
// DESTINATION PAGINATION
// ---------------------------------------------------------------------------

export function firePageEnrichment(pageDests, pageNum, profileData, userId, onComplete) {
  if (!pageDests || pageDests.length === 0) return;
  const pageKey = `page${pageNum}`;
  const matchReasonResults = {};
  const teaserResults = {};
  let completed = 0;
  const total = pageDests.length * 2;
  let finished = false;

  function finalize() {
    if (finished) return;
    finished = true;
    clearTimeout(timeout);
    const pool = JSON.parse(sessionStorage.getItem('carpePaginationPool') || '{}');
    pool[pageKey] = pageDests.map((dest, i) => ({
      ...dest,
      match_reason: matchReasonResults[i] !== undefined ? matchReasonResults[i] : '',
    }));
    sessionStorage.setItem('carpePaginationPool', JSON.stringify(pool));
    const teasers = JSON.parse(sessionStorage.getItem('carpeEnrichTeasers') || '{}');
    pageDests.forEach((dest, i) => {
      if (teaserResults[i] !== undefined) {
        teasers[dest.name] = teaserResults[i];
      }
    });
    sessionStorage.setItem('carpeEnrichTeasers', JSON.stringify(teasers));
    if (pageNum === 1) {
      sessionStorage.setItem('carpeSavedDestinations', JSON.stringify(pool[pageKey]));
    }
    if (onComplete) onComplete(pool[pageKey]);

    // Save full pool to Supabase after every page resolves —
    // ensures no enriched pages are lost on logout
    const saveId = sessionStorage.getItem('carpeCurrentSaveId');
    const userId = sessionStorage.getItem('carpeUserId');
    if (saveId && userId) {
      fetch(`${API_BASE}/auth/destinations/update-pool`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          row_id: saveId,
          pagination_pool: pool,
        }),
      }).catch(() => {});
    }
  }

  const timeout = setTimeout(finalize, 30000);

  function checkComplete() {
    completed++;
    if (completed === total) finalize();
  }

  pageDests.forEach((dest, i) => {
    fetchMatchReason(dest, profileData, userId)
      .then(reason => { matchReasonResults[i] = reason; checkComplete(); })
      .catch(() => {
        fetchMatchReason(dest, profileData, userId)
          .then(reason => { matchReasonResults[i] = reason; checkComplete(); })
          .catch(() => { matchReasonResults[i] = ''; checkComplete(); });
      });
  });

  pageDests.forEach((dest, i) => {
    fetchTeaser(dest, profileData, userId)
      .then(teasers => { teaserResults[i] = teasers; checkComplete(); })
      .catch(() => {
        fetchTeaser(dest, profileData, userId)
          .then(teasers => { teaserResults[i] = teasers; checkComplete(); })
          .catch(() => { teaserResults[i] = []; checkComplete(); });
      });
  });
}

function fetchMatchReason(dest, profileData, userId) {
  return fetch(`${API_BASE}/recommendations/destinations/enrich-match-reason`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ profile: profileData, destination: dest, user_id: userId }),
  })
    .then(res => res.json())
    .then(data => (data.destination || {}).match_reason || '');
}

function fetchTeaser(dest, profileData, userId) {
  return fetch(`${API_BASE}/recommendations/destinations/enrich-teasers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ profile: profileData, destinations: [dest], user_id: userId }),
  })
    .then(res => res.json())
    .then(data => {
      const enriched = data.enriched || [];
      return enriched.length > 0 ? (enriched[0].teaser_activities || []) : [];
    });
}

// ---------------------------------------------------------------------------
// ACTIVITY CACHE RESTORE
// ---------------------------------------------------------------------------
// Checks Supabase for a previously-saved activity_pool for this destination
// (and event_date, if provided). If found, populates carpeActivityPool and
// carpeActivityExclusions in sessionStorage so Activities.jsx's existing
// mount-time check picks it up automatically — no separate restore-rendering
// path needed. Returns true if a usable cache was found and restored, false
// otherwise (caller should fall through to the normal /loading generation flow).

export async function checkAndRestoreActivityCache(userId, destinationName, eventDate = null) {
  if (!userId || !destinationName) return false;
  try {
    const url = eventDate
      ? `${API_BASE}/auth/activities/${userId}/${encodeURIComponent(destinationName)}?event_date=${eventDate}`
      : `${API_BASE}/auth/activities/${userId}/${encodeURIComponent(destinationName)}`;
    const res = await fetch(url);
    const data = await res.json();
    if (!data.activity_pool || !data.activity_pool.page1 || data.activity_pool.page1.length === 0) {
      track('Activity Cache Checked', { destination: destinationName, has_date: !!eventDate, result: 'miss' });
      return false;
    }
    const cacheKey = eventDate ? `${destinationName}::${eventDate}` : destinationName;
    const pool = JSON.parse(sessionStorage.getItem('carpeActivityPool') || '{}');
    pool[cacheKey] = data.activity_pool;
    sessionStorage.setItem('carpeActivityPool', JSON.stringify(pool));
    const exclusions = JSON.parse(sessionStorage.getItem('carpeActivityExclusions') || '{}');
    const allNames = Object.values(data.activity_pool).flat().map(a => a.name);
    exclusions[cacheKey] = allNames;
    sessionStorage.setItem('carpeActivityExclusions', JSON.stringify(exclusions));
    track('Activity Cache Checked', { destination: destinationName, has_date: !!eventDate, result: 'hit' });
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// ACTIVITY PAGINATION
// ---------------------------------------------------------------------------
//
// Two exported functions, both called from Loading.jsx:
//
// rankActivityPage(destination, country, profile)
//   Lightweight Haiku rank — returns 5 named activities (~1.8s).
//   Loading.jsx calls this first, then immediately fires fireActivityPage
//   for page 1 AND page 2 concurrently, passing ranked names to page 1
//   and ranked names as exclusions to page 2.
//
// fireActivityPage(destination, country, profile, pageNum, excludeNames,
//                  onComplete, preRankedActivities)
//   PAGE 1: if preRankedActivities provided, skips rank and goes straight
//           to enrich. Loading.jsx always provides these after rankActivityPage.
//   PAGE 2+: no rank step. Generates + enriches with exclusion list.
//
// All network calls stay in this file. Loading.jsx is pure orchestration.
// Activities.jsx reads from carpeActivityPool and renders — no network calls.

export async function rankActivityPage(destination, country, profile, stateProvince = null, eventContext = null) {
  try {
    const res = await fetch(`${API_BASE}/recommendations/activities/rank`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ destination, country, profile, state_province: stateProvince, ...(eventContext || {}) }),
    });
    const data = await res.json();
    return data.named_activities || [];
  } catch (err) {
    return [];
  }
}

export async function fireActivityPage(
  destination,
  country,
  profile,
  pageNum,
  excludeNames,
  onComplete,
  preRankedActivities = null,
  eventContext = null, // { lat, lng, event_date } — optional, only used when events are relevant
  stateProvince = null
) {
  let finished = false;
  const pageKey = `page${pageNum}`;
  // Cache key includes date so the same destination on a different date
  // never reads/writes another date's cached activities.
  const cacheKey = eventContext?.event_date ? `${destination}::${eventContext.event_date}` : destination;

  const finalize = (activities) => {
    if (finished) return;
    finished = true;
    clearTimeout(timeoutId);

    const pool = JSON.parse(sessionStorage.getItem('carpeActivityPool') || '{}');
    if (!pool[cacheKey]) pool[cacheKey] = {};
    pool[cacheKey][pageKey] = activities;
    sessionStorage.setItem('carpeActivityPool', JSON.stringify(pool));

    const exclusions = JSON.parse(sessionStorage.getItem('carpeActivityExclusions') || '{}');
    const priorNames = exclusions[cacheKey] || [];
    const newNames = activities.map(a => a.name);
    exclusions[cacheKey] = [...priorNames, ...newNames];
    sessionStorage.setItem('carpeActivityExclusions', JSON.stringify(exclusions));
    if (onComplete) onComplete(activities, pageNum);

    if (pageNum === 1 && activities.length > 0) {
      track('Activities Generated', { destination, has_date: !!eventContext?.event_date });
    }

    // Save full pool to Supabase after every page resolves —
    // ensures no enriched pages are lost on logout. Mirrors the
    // destinations update-pool pattern.
    const userId = sessionStorage.getItem('carpeUserId');
    if (userId && activities.length > 0) {
      fetch(`${API_BASE}/auth/activities/update-pool`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          destination_name: destination,
          event_date: eventContext?.event_date || null,
          country: country || null,
          lat: eventContext?.lat || null,
          lng: eventContext?.lng || null,
          activity_pool: pool[cacheKey],
        }),
      }).catch(() => {});
    }
  };

  const timeoutId = setTimeout(() => {
    finalize([]);
  }, 35000);

  try {
    // Page 1: use preRankedActivities if provided (from Loading.jsx's rankActivityPage call),
    // so rank and enrich don't run sequentially inside this function.
    // Page 2+: no rank needed, go straight to generate-with-exclusions.
    let namedActivities = preRankedActivities || null;

    if (pageNum === 1 && !namedActivities) {
      // Fallback: rank internally if Loading.jsx didn't pre-rank
      // (shouldn't happen in normal flow, but safe to handle)
      namedActivities = await rankActivityPage(destination, country, profile, stateProvince, eventContext);
    }

    const enrichBody = namedActivities && namedActivities.length > 0
      ? { destination, country, profile, named_activities: namedActivities, state_province: stateProvince, ...(eventContext || {}) }
      : { destination, country, profile, exclude_names: excludeNames || [], count: 5, state_province: stateProvince, ...(eventContext || {}) };

    const enrichRes = await fetch(`${API_BASE}/recommendations/activities/enrich`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(enrichBody),
    });
    const enrichData = await enrichRes.json();
    const activities = enrichData.activities || [];

    finalize(activities);
    return activities;
  } catch (err) {
    finalize([]);
    return [];
  }
}